import { useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import confetti from "canvas-confetti";
import { SettlementView as SharedSettlementView } from "@tabipla/components";
import type { SettlementWithPaymentDto, MemberType } from "@tabipla/shared";
import {
  useSettlementsWithPayments,
  useCreatePayment,
  useMarkPaymentPaid,
  useConfirmPayment,
} from "@/application/hooks/usePayments";

interface SettlementViewProps {
  groupId: string;
  sessionId: string;
  currentUserId: string;
  currentUserType: MemberType;
  canEdit: boolean;
}

export function SettlementView({
  groupId,
  sessionId,
  currentUserId,
  currentUserType,
  canEdit,
}: SettlementViewProps) {
  const { t } = useTranslation();
  const { data: settlements = [], isLoading } = useSettlementsWithPayments(groupId, sessionId);

  const createPaymentMutation = useCreatePayment(groupId, sessionId);
  const markPaidMutation = useMarkPaymentPaid(groupId, sessionId);
  const confirmMutation = useConfirmPayment(groupId, sessionId);

  // Track previous allConfirmed state to detect completion
  const wasAllConfirmedRef = useRef(false);

  // Fire confetti when all settlements are confirmed
  const fireConfetti = useCallback(() => {
    // Fire multiple bursts for a celebratory effect
    const duration = 2000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 10000 };

    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }

      const particleCount = 50 * (timeLeft / duration);
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      });
    }, 250);
  }, []);

  // Check if all settlements are confirmed
  const allConfirmed =
    settlements.length > 0 && settlements.every((s) => s.paymentStatus === "confirmed");

  // Fire confetti when transitioning to all confirmed
  useEffect(() => {
    if (allConfirmed && !wasAllConfirmedRef.current) {
      fireConfetti();
    }
    wasAllConfirmedRef.current = allConfirmed;
  }, [allConfirmed, fireConfetti]);

  const handleMarkPaid = async (settlement: SettlementWithPaymentDto) => {
    try {
      if (settlement.paymentId) {
        await markPaidMutation.mutateAsync(settlement.paymentId);
      } else {
        const payment = await createPaymentMutation.mutateAsync({
          fromMemberId: settlement.fromMemberId,
          fromMemberType: settlement.fromMemberType,
          toMemberId: settlement.toMemberId,
          toMemberType: settlement.toMemberType,
          amount: settlement.amount,
          currency: settlement.currency,
        });
        if (payment) {
          await markPaidMutation.mutateAsync(payment.id);
        }
      }
      toast.success(t("payments.markedAsPaid", "支払い完了を報告しました"));
    } catch {
      toast.error(t("payments.markPaidError", "エラーが発生しました"));
    }
  };

  const handleConfirm = async (paymentId: string) => {
    try {
      await confirmMutation.mutateAsync(paymentId);
      toast.success(t("payments.confirmed", "受け取りを確認しました"));
    } catch {
      toast.error(t("payments.confirmError", "エラーが発生しました"));
    }
  };

  return (
    <SharedSettlementView
      settlements={settlements}
      isLoading={isLoading}
      currentUserId={currentUserId}
      currentUserType={currentUserType}
      canEdit={canEdit}
      onMarkPaid={handleMarkPaid}
      isMarkingPaid={markPaidMutation.isPending || createPaymentMutation.isPending}
      onConfirmReceipt={handleConfirm}
      isConfirming={confirmMutation.isPending}
      variant="avatar"
      showPayPay={true}
    />
  );
}
